import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Card, Row, Col, Button, Space, Tag, Input, Select, Empty, Tooltip, App } from 'antd';
import { 
  LinkOutlined, 
  SearchOutlined, 
  GlobalOutlined,
  TeamOutlined,
  ExperimentOutlined,
  BankOutlined,
  EyeOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const WaterConservationWebsites = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [websites, setWebsites] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const categories = [
    { value: '国家级', label: '国家级', icon: <BankOutlined />, color: '#1890ff' },
    { value: '省级', label: '省级', icon: <GlobalOutlined />, color: '#1890ff' },
    { value: '协会', label: '协会', icon: <TeamOutlined />, color: '#1890ff' },
    { value: '科研机构', label: '科研机构', icon: <ExperimentOutlined />, color: '#1890ff' },
  ];

  const loadWebsites = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/industry/websites?page=${page}&page_size=${pageSize}`;
      if (searchKeyword) {
        url += `&search=${encodeURIComponent(searchKeyword)}`;
      }
      if (selectedCategory) {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setWebsites(data.data);
        setTotal(data.total);
      } else {
        message.error('加载数据失败');
      }
    } catch (error) {
      console.error('加载网站数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchKeyword, selectedCategory]);

  useEffect(() => {
    loadWebsites();
  }, [loadWebsites]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPage(1);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setPage(1);
  };

  const handleWebsiteClick = async (website) => {
    try {
      await fetch(`${API_BASE_URL}/industry/websites/${website.id}/click`, {
        method: 'POST',
      });
      window.open(website.url, '_blank');
    } catch (error) {
      console.error('记录点击失败:', error);
      window.open(website.url, '_blank');
    }
  };

  const getCategoryInfo = (category) => {
    return categories.find(c => c.value === category) || categories[0];
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>国内水保重点网站</Title>
        <Text type="secondary">汇集全国水土保持相关官方网站</Text>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Space size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space size="middle">
            <Search
              placeholder="搜索网站名称或机构"
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
              prefix={<SearchOutlined />}
            />
            <Select
              placeholder="选择分类"
              allowClear
              style={{ width: 150 }}
              onChange={handleCategoryChange}
              value={selectedCategory || undefined}
            >
              {categories.map(cat => (
                <Option key={cat.value} value={cat.value}>
                  {cat.label}
                </Option>
              ))}
            </Select>
          </Space>
          <Text type="secondary">
            共 {total} 个网站
          </Text>
        </Space>
      </Card>

      {loading ? (
        <Card loading />
      ) : websites.length === 0 ? (
        <Card>
          <Empty description="暂无网站数据" />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {websites.map((website) => {
            const categoryInfo = getCategoryInfo(website.category);
            return (
              <Col xs={24} sm={12} md={8} lg={6} key={website.id}>
                <Card
                  hoverable
                  style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column' } }}
                  actions={[
                    <Button 
                      type="primary" 
                      size="small" 
                      icon={<LinkOutlined />}
                      onClick={() => handleWebsiteClick(website)}
                    >
                      访问网站
                    </Button>
                  ]}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '8px', 
                        backgroundColor: `${categoryInfo.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px',
                      }}>
                        {categoryInfo.icon}
                      </div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <Tooltip title={website.name}>
                          <Title level={5} style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {website.name}
                          </Title>
                        </Tooltip>
                        <Tag color={categoryInfo.color} style={{ marginTop: '4px' }}>
                          {categoryInfo.label}
                        </Tag>
                      </div>
                    </div>
                    
                    {website.organization && (
                      <Paragraph 
                        ellipsis={{ rows: 1 }} 
                        style={{ color: '#666', fontSize: '13px', marginBottom: '8px' }}
                      >
                        {website.organization}
                      </Paragraph>
                    )}
                    
                    {website.description && (
                      <Paragraph 
                        ellipsis={{ rows: 2 }} 
                        style={{ color: '#999', fontSize: '12px', marginBottom: '12px' }}
                      >
                        {website.description}
                      </Paragraph>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#999' }}>
                      <Space size="small">
                        <EyeOutlined />
                        <span>{website.clicks} 次点击</span>
                      </Space>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <Space>
          <Button 
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <Text>
            第 {page} 页 / 共 {Math.ceil(total / pageSize)} 页
          </Text>
          <Button 
            disabled={page >= Math.ceil(total / pageSize)}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default WaterConservationWebsites;
